import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext'; 
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, getDoc } from 'firebase/firestore';
import { FaMusic, FaTrash, FaPlus, FaYoutube, FaSearch, FaEdit, FaTrophy, FaGuitar, FaCloud, FaHeadphones } from 'react-icons/fa';

export default function Songs() {
  const { userDetails } = useAuth(); 
  const [songs, setSongs] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [topSongs, setTopSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMusicMinister, setIsMusicMinister] = useState(false);
  const [visibleSongsCount, setVisibleSongsCount] = useState(15);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [key, setKey] = useState('');
  const [link, setLink] = useState('');
  const [linkCifra, setLinkCifra] = useState('');
  const [linkDrive, setLinkDrive] = useState('');
  const [linkVersao, setLinkVersao] = useState('');

  const songsCollectionRef = collection(db, "songs");
  const schedulesRef = collection(db, "schedules");

  const getSongs = async () => {
    setLoading(true);
    try {
      // 1. Pegar Músicas
      const songsData = await getDocs(songsCollectionRef);
      const allSongs = songsData.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

      // 2. Pegar Escalas (Para contar popularidade do Mês Atual)
      const schedData = await getDocs(schedulesRef);
      const songCounts = {};
      
      // Define o mês atual YYYY-MM
      const currentMonthStr = new Date().toISOString().slice(0, 7);

      schedData.docs.forEach(doc => {
        const sched = doc.data();
        // === FILTRO: Só conta se a escala for do mês atual ===
        if (sched.date && sched.date.startsWith(currentMonthStr)) {
            if (sched.repertoire) {
                sched.repertoire.forEach(song => {
                    songCounts[song.id] = (songCounts[song.id] || 0) + 1;
                });
            }
        }
      });

      // Adiciona contagem timesPlayed a cada música
      const songsWithCount = allSongs.map(song => ({
        ...song,
        timesPlayed: songCounts[song.id] || 0
      }));

      // 1. Músicas do Repertório Geral: Ordenadas alfabeticamente
      const sortedAlphabetically = [...songsWithCount].sort((a, b) => a.title.localeCompare(b.title));
      setSongs(sortedAlphabetically);
      setFilteredSongs(sortedAlphabetically);
      
      // 2. Top Músicas (Destaques): Apenas músicas tocadas pelo menos 1 vez, ordenadas por execuções desc e depois alfabeticamente
      const topRanked = [...songsWithCount]
        .filter(s => s.timesPlayed > 0)
        .sort((a, b) => b.timesPlayed - a.timesPlayed || a.title.localeCompare(b.title));
      setTopSongs(topRanked.slice(0, 5)); 

    } catch (error) {
      console.error("Erro ao buscar músicas:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    getSongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkMusicMinisterStatus = async () => {
      if (userDetails?.isAdmin) {
        setIsMusicMinister(true);
        return;
      }
      if (userDetails?.musicianId) {
        try {
          const docRef = doc(db, "musicians", userDetails.musicianId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            const roles = data.roles || [];
            if (
              roles.includes("Ministro de Música") ||
              data.primaryRole === "Ministro de Música" ||
              data.role === "Ministro de Música"
            ) {
              setIsMusicMinister(true);
              return;
            }
          }
        } catch (error) {
          console.error("Erro ao verificar papel do músico:", error);
        }
      }
      setIsMusicMinister(false);
    };
    checkMusicMinisterStatus();
  }, [userDetails]);

  useEffect(() => {
    if (searchTerm === '') {
        setFilteredSongs(songs); 
    } else {
        const results = songs.filter(s => 
            s.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.artist?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredSongs(results);
    }
    // Reseta o contador de músicas visíveis ao mudar a busca
    setVisibleSongsCount(15);
  }, [searchTerm, songs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) return;
    if (!userDetails?.isAdmin && !isMusicMinister) {
      alert("Você não tem permissão para cadastrar ou editar músicas.");
      return;
    }
    try {
      const songData = { 
        title, 
        artist, 
        key, 
        link, 
        linkCifra, 
        linkDrive, 
        linkVersao,
        updatedAt: new Date() 
      };
      if (editId) {
        await updateDoc(doc(db, "songs", editId), songData);
      } else {
        await addDoc(songsCollectionRef, { ...songData, createdAt: new Date() });
      }
      handleCancel();
      getSongs();
    } catch (error) { console.error(error); }
  };

  const handleEdit = (song) => {
    setTitle(song.title); 
    setArtist(song.artist || ''); 
    setKey(song.key || ''); 
    setLink(song.link || '');
    setLinkCifra(song.linkCifra || '');
    setLinkDrive(song.linkDrive || '');
    setLinkVersao(song.linkVersao || '');
    setEditId(song.id); 
    setIsFormOpen(true); 
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    setTitle(''); 
    setArtist(''); 
    setKey(''); 
    setLink('');
    setLinkCifra('');
    setLinkDrive('');
    setLinkVersao('');
    setEditId(null); 
    setIsFormOpen(false);
  };

  const handleDelete = async (id) => {
    if (!userDetails?.isAdmin && !isMusicMinister) {
      alert("Você não tem permissão para remover músicas.");
      return;
    }
    if (window.confirm("Excluir esta música do repertório?")) {
      try {
        await deleteDoc(doc(db, "songs", id));
        getSongs();
      } catch (error) {
        console.error(error);
        alert("Erro ao excluir música: " + error.message);
      }
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div>
            <h1 className="text-2xl font-bold text-gray-800">Repertório</h1>
            <p className="text-sm text-gray-500">
                {searchTerm ? 'Buscando músicas...' : 'Gerencie o repertório da igreja'}
            </p>
        </div>
        
        <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative w-full md:w-64">
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                <input 
                    type="text" placeholder="Buscar música..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 rounded-lg border focus:border-brand-blue outline-none shadow-sm"
                />
            </div>
            {(userDetails?.isAdmin || isMusicMinister) && (
                <button 
                onClick={() => { if(isFormOpen) handleCancel(); else setIsFormOpen(true); }}
                className="bg-brand-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-800 transition shadow flex-shrink-0"
                >
                <FaPlus />
                <span className="hidden sm:inline">{isFormOpen ? 'Cancelar' : 'Nova'}</span>
                </button>
            )}
        </div>
      </div>

      {/* Formulário apenas para Admin ou Ministro de Música */}
      {isFormOpen && (userDetails?.isAdmin || isMusicMinister) && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6 border-l-4 border-brand-gold animate-fade-in">
          <h3 className="text-lg font-bold mb-4 text-gray-700">{editId ? 'Editar Música' : 'Adicionar ao Repertório'}</h3>
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-600 mb-1">Nome da Música</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border p-2 rounded focus:border-brand-blue outline-none" required />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Artista / Versão</label>
              <input value={artist} onChange={(e) => setArtist(e.target.value)} className="w-full border p-2 rounded focus:border-brand-blue outline-none" />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Tom</label>
              <select value={key} onChange={(e) => setKey(e.target.value)} className="w-full border p-2 rounded focus:border-brand-blue outline-none bg-white">
                <option value="">Selecione...</option>
                {['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Link YouTube</label>
              <div className="flex items-center border rounded focus-within:border-brand-blue bg-white">
                <span className="p-2 text-red-500"><FaYoutube /></span>
                <input value={link} onChange={(e) => setLink(e.target.value)} className="w-full p-2 outline-none rounded" placeholder="https://youtube.com/..." />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Link Cifra</label>
              <div className="flex items-center border rounded focus-within:border-brand-blue bg-white">
                <span className="p-2 text-orange-500"><FaGuitar /></span>
                <input value={linkCifra} onChange={(e) => setLinkCifra(e.target.value)} className="w-full p-2 outline-none rounded" placeholder="https://cifraclub.com.br/..." />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-600 mb-1">Link Google Drive (Partitura / Arquivos)</label>
              <div className="flex items-center border rounded focus-within:border-brand-blue bg-white">
                <span className="p-2 text-blue-500"><FaCloud /></span>
                <input value={linkDrive} onChange={(e) => setLinkDrive(e.target.value)} className="w-full p-2 outline-none rounded" placeholder="https://drive.google.com/..." />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-gray-600 mb-1">Link Nossa Versão</label>
              <div className="flex items-center border rounded focus-within:border-brand-blue bg-white">
                <span className="p-2 text-purple-500"><FaHeadphones /></span>
                <input value={linkVersao} onChange={(e) => setLinkVersao(e.target.value)} className="w-full p-2 outline-none rounded" placeholder="https://..." />
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end mt-2 gap-2">
              {editId && <button type="button" onClick={handleCancel} className="bg-gray-200 text-gray-700 font-bold px-6 py-2 rounded hover:bg-gray-300">Cancelar</button>}
              <button type="submit" className="bg-brand-gold text-brand-blue font-bold px-8 py-2 rounded shadow hover:opacity-90 transition">{editId ? 'Atualizar' : 'Salvar Música'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Grid de Cards de Listagem */}
      {loading ? <div className="text-center p-10 text-gray-500 animate-pulse">Carregando repertório...</div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          
          {/* Card Principal: Repertório Geral */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow overflow-hidden border border-gray-100">
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
              <h3 className="font-bold text-gray-700 flex items-center gap-2">
                <FaMusic className="text-brand-blue" />
                Repertório Geral ({filteredSongs.length})
              </h3>
            </div>
            
            {filteredSongs.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                {searchTerm ? 'Nenhuma música encontrada.' : 'Nenhuma música cadastrada.'}
              </div>
            ) : (
              <>
                <ul>
                  {filteredSongs.slice(0, visibleSongsCount).map((song, index) => {
                    const isLastItem = index === Math.min(filteredSongs.length, visibleSongsCount) - 1;
                    return (
                      <li key={song.id} className={`p-4 flex justify-between items-center ${!isLastItem ? 'border-b' : ''} hover:bg-gray-50 transition-colors`}>
                        <div className="flex items-center gap-3 overflow-hidden w-full">
                          <div className="p-3 rounded-full bg-blue-50 text-brand-blue flex-shrink-0">
                            <FaMusic size={14} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-bold text-gray-800 truncate">{song.title}</h4>
                            
                            {/* Linha de Metadados: Artista e Tom */}
                            <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                              <span className="truncate">{song.artist || 'Artista desc.'}</span>
                              {song.key && <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-bold flex-shrink-0">Tom: {song.key}</span>}
                            </div>
                            
                            {/* Ações Mobile: Exibidas em uma linha própria abaixo do Tom no mobile */}
                            <div className="flex md:hidden items-center gap-4 mt-2 flex-wrap">
                              {song.link && (
                                <a href={song.link} target="_blank" rel="noreferrer" className="text-red-500 p-1 hover:opacity-80 transition" title="YouTube">
                                  <FaYoutube size={16} />
                                </a>
                              )}
                              {song.linkCifra && (
                                <a href={song.linkCifra} target="_blank" rel="noreferrer" className="text-orange-500 p-1 hover:opacity-80 transition" title="Cifra">
                                  <FaGuitar size={16} />
                                </a>
                              )}
                              {song.linkDrive && (
                                <a href={song.linkDrive} target="_blank" rel="noreferrer" className="text-blue-500 p-1 hover:opacity-80 transition" title="Google Drive">
                                  <FaCloud size={16} />
                                </a>
                              )}
                              {song.linkVersao && (
                                <a href={song.linkVersao} target="_blank" rel="noreferrer" className="text-purple-500 p-1 hover:opacity-80 transition" title="Nossa Versão">
                                  <FaHeadphones size={16} />
                                </a>
                              )}
                              {(userDetails?.isAdmin || isMusicMinister) && (
                                <div className="flex items-center gap-3 ml-2 border-l pl-3 border-gray-200">
                                  <button onClick={() => handleEdit(song)} className="text-blue-300 hover:text-blue-500 p-1 transition" title="Editar">
                                    <FaEdit size={16} />
                                  </button>
                                  <button onClick={() => handleDelete(song.id)} className="text-gray-300 hover:text-red-500 p-1 transition" title="Excluir">
                                    <FaTrash size={16} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Ações Desktop: Exibidas no lado direito */}
                        <div className="hidden md:flex items-center gap-2.5 flex-shrink-0 ml-4">
                          {song.link && <a href={song.link} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-red-500 transition p-1.5" title="YouTube"><FaYoutube size={16} /></a>}
                          {song.linkCifra && <a href={song.linkCifra} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-orange-500 transition p-1.5" title="Cifra"><FaGuitar size={15} /></a>}
                          {song.linkDrive && <a href={song.linkDrive} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-500 transition p-1.5" title="Google Drive"><FaCloud size={15} /></a>}
                          {song.linkVersao && <a href={song.linkVersao} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-purple-500 transition p-1.5" title="Nossa Versão"><FaHeadphones size={15} /></a>}
                          
                          {(userDetails?.isAdmin || isMusicMinister) && (
                              <div className="flex items-center gap-1 border-l pl-2.5 border-gray-200">
                                  <button onClick={() => handleEdit(song)} className="text-blue-300 hover:text-blue-500 transition p-1.5" title="Editar"><FaEdit size={14} /></button>
                                  <button onClick={() => handleDelete(song.id)} className="text-gray-300 hover:text-red-500 transition p-1.5" title="Excluir"><FaTrash size={14} /></button>
                              </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                
                {filteredSongs.length > visibleSongsCount && (
                  <div className="p-4 border-t text-center bg-gray-50">
                    <button 
                      onClick={() => setVisibleSongsCount(prev => prev + 15)}
                      className="text-brand-blue font-bold hover:text-blue-800 transition text-sm py-1.5 px-6 border border-brand-blue rounded-full bg-white shadow-sm hover:shadow active:scale-95 duration-150 inline-flex items-center gap-2"
                    >
                      Carregar Mais (+15)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Card Lateral: Mais Tocadas do Mês */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow overflow-hidden border border-gray-100">
            <div className="p-4 bg-yellow-50 border-b border-yellow-100 flex items-center gap-2">
              <FaTrophy className="text-yellow-600 animate-bounce" />
              <h3 className="font-bold text-yellow-800">Mais Tocadas do Mês</h3>
            </div>
            
            {topSongs.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400 italic">
                Nenhuma música tocada este mês ainda.
              </div>
            ) : (
              <ul>
                {topSongs.map((song, index) => (
                  <li key={song.id} className={`p-4 flex justify-between items-center ${index !== topSongs.length - 1 ? 'border-b' : ''} hover:bg-gray-50 transition-colors`}>
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="p-2.5 rounded-full bg-yellow-100 text-yellow-600 font-bold text-sm w-9 h-9 flex items-center justify-center flex-shrink-0">
                        #{index + 1}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-gray-800 text-sm truncate">{song.title}</h4>
                        <p className="text-xs text-gray-500 truncate">{song.artist || 'Artista desc.'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {song.link && <a href={song.link} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-red-500 transition p-1" title="YouTube"><FaYoutube size={14} /></a>}
                      {song.linkCifra && <a href={song.linkCifra} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-orange-500 transition p-1" title="Cifra"><FaGuitar size={13} /></a>}
                      {song.linkDrive && <a href={song.linkDrive} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-500 transition p-1" title="Google Drive"><FaCloud size={13} /></a>}
                      {song.linkVersao && <a href={song.linkVersao} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-purple-500 transition p-1" title="Nossa Versão"><FaHeadphones size={13} /></a>}
                      {song.key && <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded text-xs font-bold">Tom: {song.key}</span>}
                      <span className="bg-brand-blue/10 text-brand-blue text-xs font-bold px-2 py-0.5 rounded border border-brand-blue/20" title="Execuções no mês">
                        {song.timesPlayed}x
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

        </div>
      )}
    </div>
  );
}